import { renderPageShell } from '../lib/page-shell';
import { workerStackTowerMatchSocketUrl, workerStackTowerRankedQueueUrl } from '../lib/worker-config';
import { fetchMe, mintConnectionTicket } from '../lib/session';
import { attachStackTowerMatchSession } from '../lib/stack-tower-match-session';
import type { RouteHandler } from '../lib/router';

/**
 * C015 Phase 6 — ranked Stack Tower matchmaking, structurally
 * `ranked-duel.ts` retargeted at the Stack Tower queue/match sockets. Sign-in
 * (and, server-side, a verified email) is required — there is no guest path
 * here, unlike the casual room-code mode.
 */
export const renderStackTowerRanked: RouteHandler = (container, navigate) => {
  const body = renderPageShell(container, navigate, 'play', {
    title: 'STACK TOWER: RANKED',
    subtitle: '60 seconds against a matched opponent. Rating updates after the match.',
    body: `<div id="stack-tower-ranked-mount"></div>`,
  });

  const mount = body.querySelector<HTMLDivElement>('#stack-tower-ranked-mount')!;
  let queueSocket: WebSocket | null = null;
  let matchSocket: WebSocket | null = null;
  let destroySession: (() => void) | void;

  function cleanup() {
    if (destroySession) destroySession();
    destroySession = undefined;
    if (queueSocket) {
      queueSocket.onmessage = null;
      queueSocket.onclose = null;
      queueSocket.onerror = null;
      queueSocket.close();
      queueSocket = null;
    }
    if (matchSocket) {
      matchSocket.onclose = null;
      matchSocket.close();
      matchSocket = null;
    }
  }

  function renderSignInGate() {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Sign in required</p>
        <h2>Ranked Stack Tower needs a verified account</h2>
        <button class="button button--primary" type="button" data-action="account">Go to account</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="account"]')?.addEventListener('click', () => navigate('account'));
  }

  function renderSearching() {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Searching for an opponent…</p>
        <button class="button button--secondary" type="button" data-action="cancel">Cancel</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="cancel"]')?.addEventListener('click', () => {
      cleanup();
      navigate('play');
    });
  }

  function renderError(message: string) {
    mount.innerHTML = `
      <div class="handoff">
        <p class="handoff__label">Error</p>
        <h2>${message}</h2>
        <button class="button button--secondary" type="button" data-action="menu">Back to menu</button>
      </div>
    `;
    mount.querySelector<HTMLButtonElement>('[data-action="menu"]')?.addEventListener('click', () => {
      cleanup();
      navigate('play');
    });
  }

  async function joinMatch(code: string, ticket: string | null, name: string) {
    if (queueSocket) {
      queueSocket.onmessage = null;
      queueSocket.onclose = null;
      queueSocket = null;
    }
    // The queue socket's ticket is spent; mint a fresh one for the match socket.
    let connectionTicket: string;
    try {
      connectionTicket = await mintConnectionTicket();
    } catch {
      renderSignInGate();
      return;
    }
    matchSocket = new WebSocket(workerStackTowerMatchSocketUrl(code, name, connectionTicket, ticket));
    matchSocket.onclose = () => {
      matchSocket = null;
    };
    destroySession = attachStackTowerMatchSession(mount, matchSocket, {
      lobbyHeading: 'Ranked match found',
      onExit: () => {
        cleanup();
        navigate('play');
      },
    });
  }

  async function startQueue() {
    const me = await fetchMe();
    if (!me) {
      renderSignInGate();
      return;
    }

    renderSearching();
    let connectionTicket: string;
    try {
      connectionTicket = await mintConnectionTicket();
    } catch {
      renderSignInGate();
      return;
    }
    queueSocket = new WebSocket(workerStackTowerRankedQueueUrl(connectionTicket));
    queueSocket.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          type: string;
          code?: string;
          ticket?: string;
          message?: string;
        };
        if (msg.type === 'matched' && msg.code) {
          // The ticket is the only thing that makes this match ranked. Without
          // it the room plays, and scores, as casual.
          void joinMatch(msg.code, msg.ticket ?? null, me.user.username);
        }
        if (msg.type === 'queue-error') {
          renderError(msg.message ?? 'Could not start a ranked match. Please queue again.');
        }
      } catch {
        // ignore malformed frames
      }
    };
    queueSocket.onerror = () => renderError('Could not reach the ranked queue. A verified email is required.');
  }

  void startQueue();

  return () => cleanup();
};
